import { ThunkDispatch } from '@reduxjs/toolkit';
import React from 'react';
import Autocomplete from 'react-autocomplete';
import { MapDispatchToProps, connect, MapStateToProps } from 'react-redux';
import { FormikProps, FieldArray, Formik, Form, FormikActions } from 'formik';
import { RootState } from 'store/store';
import {
  removeDuplicates,
  removeDuplicateObjectsFromArray,
} from 'utils/happyFunctions';
import { englishNumber, convertDigitToProperFormat } from 'utils/number';
import messages from './Messages';
import Button from 'components/Button';
import { display } from 'components/ErrorView/ErrorDialog';
import { IOption } from 'components/Select/Select';
import LoadingIcon from 'components/LoadingIcon';
import { MenuItem } from 'components/TaskSearch/styles';
import CompanyInviteView from './CompanyInviteView';
import {
  SearchIcon,
  ClearInputIcon,
  MembersCountInfo,
  InviteMembersButton,
  inviteListFormStyle,
  InvitedMemberInfoBox,
  InviteMembersActions,
  SearchPhoneNumberBox,
  InvitedMemberInfoIcon,
  SearchPhoneNumberInput,
  InviteSearchLoadingStyle,
  InviteMemberViewsWrapper,
  searchPhoneNumberMenuStyle,
  SearchPhoneNumberContainer,
  InvitedMemberInfoContainer,
} from './styles';
import notify from 'components/Notify';
import { IUser } from 'store/users/usersType';
import { searchUser, searchUserFn } from 'store/users/usersSlice';
import { ICompanyMember, IInvitedMember } from 'store/companies/companiesTypes';
import {
  addMemberToCompany,
  addMemberToCompanyFn,
  batchAddMembersToCompany,
  batchAddMembersToCompanyFn,
} from 'store/companies/companiesSlice';
import i18n from '../../../i18n';

interface IOwnProps {
  isActive: boolean;
  companyId: number;
  companyName: string;
  companyRoleChips: string[];
  selectableCompanyRoleChips: string[];
  filterUserWithName: (name: string) => void;
}

interface IComponentStates {
  loading: boolean;
  searching: boolean;
  forceRename: boolean;
  searchResult: IUser[];
  inviteMember: boolean;
  conflictUsers: IUser[] | null;
  numberToSearch: string;
}

interface IStateToProps {
  canEditUser: boolean;
  canCreateUser: boolean;
  currentUserNames: string[];
  phoneToMemberMap: { [key: string]: ICompanyMember };
}

interface IDispatchToProps {
  searchSingleUser: (phoneNumber: string) => ReturnType<typeof searchUserFn>;
  addSingleMemberToCompany: (
    companyMember: ICompanyMember
  ) => ReturnType<typeof addMemberToCompanyFn>;
  addMultipleMembersToCompany: (
    invitedMembers: IInvitedMember[],
    forceRename: boolean,
    companyId: number
  ) => ReturnType<typeof batchAddMembersToCompanyFn>;
}

type IComponentProps = IOwnProps & IStateToProps & IDispatchToProps;

interface IItem {
  id: number;
  name: string;
  phoneNumber?: string;
}

export interface IInvitedMemberValues {
  roles: string[];
  members: IInvitedMember[];
}

const convertToChip = (role: string): IOption => {
  return { value: role, label: role };
};

class CompanyInviteList extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  state: IComponentStates = {
    loading: false,
    searching: false,
    forceRename: this.props.canEditUser,
    searchResult: [],
    inviteMember: false,
    conflictUsers: [],
    numberToSearch: '',
  };

  timer: any = undefined;

  onSelectUser =
    (addValueAtFirst: (value: IInvitedMember) => number) =>
    (_value: string, item: IItem) => {
      // const { companyId, companyName } = this.props;

      if (item.phoneNumber) {
        addValueAtFirst({
          name: item.name,
          roles: [],
          phoneNumber: item.phoneNumber || _value,
          toDeleteRoleIds: [],
          employeeId: '',
        });
        this.clearInputState();
      } else {
        const companyMember = {
          userId: item.id,
          companyId: Number(this.props.companyId),
        } as ICompanyMember;
        this.props.addSingleMemberToCompany(companyMember).then(
          () => {
            notify(
              messages.memberAddedToCompany(item.name, this.props.companyName)
            );
            this.clearInputState();
          },
          (err: any) => {
            display(err);
          }
        );
      }
    };

  clearInputState = () => {
    this.setState({ numberToSearch: '', searchResult: [] });
  };

  getAutoCompleteItem = (item: IItem) => {
    return item.name;
  };

  handleChangeSearchInput = (
    _e: React.ChangeEvent<HTMLInputElement>,
    numberToSearch: string
  ) => {
    clearTimeout(this.timer);
    const phoneNumber: string = convertDigitToProperFormat(numberToSearch);
    this.setState({ numberToSearch: phoneNumber });
    if (numberToSearch !== '') {
      this.timer = setTimeout(this.triggerSearch, 700);
    }
  };

  triggerSearch = () => {
    this.setState({ searching: true });
    const phoneNumber = englishNumber(this.state.numberToSearch);
    this.props.searchSingleUser(phoneNumber).then(
      ({ users }) => {
        if (users.length > 0) {
          this.setState({
            searching: false,
            searchResult: users,
            inviteMember: false,
          });
        } else {
          const newPhoneNumber = /^09\d{9}$/.test(phoneNumber)
            ? ([{ name: messages.invitePhoneNumber(phoneNumber) }] as IUser[])
            : [];
          this.setState({
            searching: false,
            searchResult: newPhoneNumber,
            inviteMember: true,
          });
        }
      },
      (err: any) => {
        display(err);
      }
    );
  };

  renderAutocompleteItem = (item: IItem, isHighlighted: boolean) => {
    return (
      <MenuItem key={`${item.id}_${item.name}`} isHighlighted={isHighlighted}>
        {convertDigitToProperFormat(item.name)}
      </MenuItem>
    );
  };

  renderAutocompleteMenu = (
    items: IItem[],
    value: string,
    _styles: React.CSSProperties
  ): React.ReactNode => {
    return (
      <ul key={value} style={searchPhoneNumberMenuStyle}>
        {items as any}
      </ul>
    );
  };

  removeWhiteSpaces = (str: string) => str.trim().replace(/\s+/, ' ');

  onPasteIntoTextArea =
    (pushMember: (values: IInvitedMemberValues) => void) =>
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const htmlTable = e.clipboardData.getData('text/html');
      const isTableExists = htmlTable.search(/<table/gm);

      if (isTableExists !== -1) {
        const parsed = new DOMParser().parseFromString(htmlTable, 'text/html');
        let filteredRows = Array.from(parsed.getElementsByTagName('tr'));

        /*
        detecting header row by "last name" column title
        if its title change, update here
      */
        const hasHeader = filteredRows[0].innerText.includes(
          i18n.t('headerLastNameColumn', { ns: 'companyManagement' })
        );
        // delete header row
        if (hasHeader) {
          filteredRows.shift();
        }

        // filter empty rows
        filteredRows = filteredRows.filter((r) => r.innerText !== '');

        let cells: string[][] = [];
        cells = (filteredRows as any).map((row: any) => {
          return Array.from(
            new Set(row.getElementsByTagName('td')),
            (td: any) => td.innerText
          );
        });

        // remove duplicates rows
        cells = Array.from(
          new Set(cells.map(JSON.stringify as any)) as any,
          JSON.parse as any
        );

        const newRoles: string[] = [];
        const newRolesId: string[] = []; // removed all whitespaces
        const crIdMap = this.props.companyRoleChips.map((cr) =>
          cr.replaceAll(/\s/g, '')
        );
        const invitedMembers = cells.map((cell) => {
          const roles = cell[3]
            ? cell[3]
                .split('/')
                .map(this.removeWhiteSpaces)
                .filter((it) => it.length)
            : [];
          roles.forEach((r, index) => {
            const rid = r.replaceAll(/\s/g, '');
            const foundInCurrent = crIdMap.indexOf(rid);
            if (foundInCurrent > 0) {
              roles[index] = this.props.companyRoleChips[foundInCurrent];
            } else {
              const foundInNewRoles = newRolesId.indexOf(rid);
              if (foundInNewRoles > 0) {
                roles[index] = newRoles[foundInNewRoles];
              } else {
                newRoles.push(r);
                newRolesId.push(rid);
              }
            }
          });
          let phone = cell[2];
          if (phone.length < 11 && phone.length > 1 && phone[0] !== '0')
            phone = '0' + phone;
          const name = `${cell[0].trim()} ${cell[1].trim()}`;
          const employeeId = cell[4].trim();

          return {
            name: name, // firstName + lastName
            roles, // string roles
            phoneNumber: phone,
            employeeId,
          };
        });

        // remove duplicate phone numbers
        const uniqueInvitedMembers = removeDuplicateObjectsFromArray(
          invitedMembers as IInvitedMember[],
          'phoneNumber'
        );
        pushMember({
          roles: removeDuplicates([
            ...this.props.companyRoleChips,
            ...newRoles,
          ]),
          members: uniqueInvitedMembers as Array<IInvitedMember>,
        });
      } else {
        const pastedText = convertDigitToProperFormat(
          e.clipboardData.getData('text/plain')
        );
        this.setState({ numberToSearch: pastedText }, this.triggerSearch);
      }
    };

  handleSubmitInviteMembers = (values: IInvitedMemberValues) => {
    this.setState({ loading: true });
    const { companyId, companyName, addMultipleMembersToCompany } = this.props;
    addMultipleMembersToCompany(
      values.members,
      this.state.forceRename,
      companyId
    )
      .then(({ users }) => {
        this.setState({ loading: false });
        const conflictUsers = users.filter((user: IUser) => {
          const importedUser = values.members.find(
            (member) => member.phoneNumber === user.phoneNumber
          ) as IInvitedMember;
          return importedUser.name !== user.name;
        });
        if (conflictUsers.length > 0) {
          this.setState({ conflictUsers });
        }
        notify(
          messages.inviteMembersSuccess(
            convertDigitToProperFormat(values.members.length),
            companyName
          )
        );
      })
      .catch((err: any) => {
        this.setState({ loading: false });
        display(err);
      });
  };

  handleAddAMember =
    (
      members: IInvitedMember[],
      addValueAtFirst: (value: IInvitedMember) => number
    ) =>
    () => {
      const { numberToSearch } = this.state;
      const phoneNumber = englishNumber(numberToSearch);
      if (
        members.length > 0 &&
        members.find((m) => m.phoneNumber === phoneNumber)
      ) {
        notify(
          i18n.t('phoneNumberExists', { ns: 'companyManagement' }),
          'warning'
        );
        return;
      }
      addValueAtFirst({
        name: '',
        roles: [],
        phoneNumber,
        toDeleteRoleIds: [],
        employeeId: '',
      });
      this.clearInputState();
    };

  phoneNumberInput = (props: any) => {
    const { ref, ...restProps } = props;
    return <SearchPhoneNumberInput ref={ref} {...restProps} />;
  };

  clearConflicts =
    (clearMembers: FormikActions<IInvitedMemberValues>['resetForm']) => () => {
      this.setState(
        {
          conflictUsers: null,
        },
        clearMembers
      );
    };

  // handleChangeForceRename = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   this.setState({ forceRename: e.target.checked });
  // };

  render() {
    const { isActive } = this.props;
    const {
      loading,
      searching,
      inviteMember,
      searchResult,
      conflictUsers,
      numberToSearch,
    } = this.state;
    const { currentUserNames } = this.props;
    const hasConflict = conflictUsers && conflictUsers.length > 0;
    return (
      <Formik
        onSubmit={this.handleSubmitInviteMembers}
        initialValues={{
          members: [],
          roles: this.props.selectableCompanyRoleChips,
        }}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {(formikBag: FormikProps<IInvitedMemberValues>) => {
          const {
            setValues,
            setFieldValue,
            values: { members, roles },
          } = formikBag;
          const isMultiple = members.length > 1;
          return (
            // @ts-expect-error JSX error
            <Form
              style={{
                ...inviteListFormStyle,
                display: isActive ? 'flex' : 'none',
              }}
            >
              {hasConflict ? (
                <>
                  <InvitedMemberInfoContainer>
                    {conflictUsers!.map((c) => {
                      const importedUser = members.find(
                        (member) => member.phoneNumber === c.phoneNumber
                      ) as IInvitedMember;
                      if (importedUser.name === c.name) return <></>;
                      return (
                        <InvitedMemberInfoBox key={c.phoneNumber as string}>
                          <InvitedMemberInfoIcon type="alert-circle" />
                          <span>
                            {messages.conflictUser(importedUser.name, c.name)}
                          </span>
                        </InvitedMemberInfoBox>
                      );
                    })}
                  </InvitedMemberInfoContainer>
                  <Button
                    color="blue-outline"
                    onClick={this.clearConflicts(formikBag.resetForm)}
                  >
                    {i18n.t('gotIt', { ns: 'companyManagement' }) as string}
                  </Button>
                </>
              ) : (
                <FieldArray name="members">
                  {({ remove, unshift }) => (
                    <>
                      <SearchPhoneNumberContainer>
                        <SearchPhoneNumberBox>
                          <Autocomplete
                            items={searchResult}
                            value={numberToSearch}
                            onChange={this.handleChangeSearchInput}
                            renderMenu={this.renderAutocompleteMenu as any}
                            renderItem={this.renderAutocompleteItem}
                            renderInput={this.phoneNumberInput}
                            getItemValue={this.getAutoCompleteItem}
                            onSelect={
                              inviteMember
                                ? this.handleAddAMember(members, unshift)
                                : this.onSelectUser(unshift)
                            }
                            inputProps={{
                              onPaste: this.onPasteIntoTextArea(setValues),
                              placeholder: i18n.t('searchUserByPhoneNumber', {
                                ns: 'companyManagement',
                              }) as string,
                            }}
                            wrapperStyle={{
                              width: '18rem',
                              position: 'relative',
                            }}
                          />
                          <SearchIcon
                            type="phone"
                            className="disable-pointer-events"
                          />
                          {!!numberToSearch &&
                            (searching ? (
                              <LoadingIcon
                                customStyle={InviteSearchLoadingStyle}
                              />
                            ) : (
                              <ClearInputIcon
                                type="x"
                                onClick={this.clearInputState}
                              />
                            ))}
                        </SearchPhoneNumberBox>
                        {isMultiple && (
                          <MembersCountInfo>
                            <span>
                              {
                                i18n.t('countOfMembers', {
                                  ns: 'companyManagement',
                                }) as string
                              }
                            </span>
                            <span>
                              {convertDigitToProperFormat(members.length)}
                            </span>
                          </MembersCountInfo>
                        )}
                      </SearchPhoneNumberContainer>
                      <InviteMemberViewsWrapper>
                        {members.map((item, index) => (
                          <CompanyInviteView
                            key={`members.${index}`}
                            item={item}
                            remove={() => remove(index)}
                            indexKey={index}
                            companyRoles={roles.map(convertToChip)}
                            defaultRoles={item.roles.map(convertToChip)}
                            onChangeValue={setFieldValue}
                            currentUserNames={currentUserNames}
                            filterUserWithName={this.props.filterUserWithName}
                            memberWithSamePhone={
                              this.props.phoneToMemberMap[item.phoneNumber]
                            }
                            toDeleteRoleIds={item.toDeleteRoleIds}
                          />
                        ))}
                      </InviteMemberViewsWrapper>
                      {members.length > 0 && (
                        <InviteMembersActions>
                          <InviteMembersButton
                            type="submit"
                            color="green"
                            loading={loading}
                          >
                            {messages.inviteMembers(isMultiple)}
                          </InviteMembersButton>
                          {/*{canEditUser && (
                            <Input
                              id="forceRename"
                              type="checkbox"
                              label={messages.forceRename}
                              checked={this.state.forceRename}
                              onChange={this.handleChangeForceRename}
                              wrapperClassName="force-rename"
                            />
                          )}*/}
                        </InviteMembersActions>
                      )}
                    </>
                  )}
                </FieldArray>
              )}
            </Form>
          );
        }}
      </Formik>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  ownProps
) => {
  const thisUserPermissions = state.thisUser.permissions;
  const canEditUser = true;
  let canCreateUser = false;
  // check permissions is in localStorage
  if (thisUserPermissions) {
    // canEditUser = thisUserPermissions.includes('Edit_User');
    canCreateUser = thisUserPermissions.includes('Create_User');
  }
  const currentUserNames: string[] = Object.keys(
    state.companies.data[ownProps.companyId].members
  ).map((key: string) => {
    return state.users.map[key]?.name || '';
  });

  const phoneToMemberMap: { [key: string]: ICompanyMember } = {};
  Object.values(state.companies.data[ownProps.companyId].members).forEach(
    (it: any) => {
      const phone = state.users.map[it.userId]?.phoneNumber;
      if (!phone) return;
      phoneToMemberMap[phone] = it;
    }
  );

  return { canEditUser, canCreateUser, currentUserNames, phoneToMemberMap };
};

const mapDispatchToProps: MapDispatchToProps<IDispatchToProps, IOwnProps> = (
  dispatch: ThunkDispatch<any, never, any>
) => ({
  searchSingleUser: (phoneNumber: string) =>
    dispatch(searchUser({ phoneOrEmail: phoneNumber })).unwrap(),
  addSingleMemberToCompany: (companyMember: ICompanyMember) =>
    dispatch(addMemberToCompany({ companyMember })).unwrap(),
  addMultipleMembersToCompany: (
    invitedMembers: IInvitedMember[],
    forceRename: boolean,
    companyId: number
  ) =>
    dispatch(
      batchAddMembersToCompany({ invitedMembers, forceRename, companyId })
    ).unwrap(),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyInviteList);
